import { Suspense, lazy, useState } from 'react';
import { m } from 'motion/react';
import { formatBytes, formatEta, formatPercent, formatRate } from '../core/format';
import type { AppError, DownloadMode } from '../core/types';
import { engine, useApp } from '../state/store';
import { exportMode, exportOriginal } from '../engine/export';
import { decidePlan } from './hooks/useMediaPipeline';
import { useSession } from './hooks/useSession';
import { CoverageRail } from './parts/CoverageRail';
import { Chip, Figure } from './parts/bits';
import {
  ArrowClockwise,
  DownloadSimple,
  Info,
  Pause,
  Play,
  Trash,
  Warning,
  KindIcon,
} from './parts/Icons';
import { StatusStage } from './stages/StatusStage';
import { MediaStage } from './stages/MediaStage';
import { ImageStage } from './stages/ImageStage';
import { TextStage } from './stages/TextStage';
import { Queue } from './Queue';

// Heavy viewers load only when a file of that type is opened.
const PdfStage = lazy(() => import('./stages/PdfStage').then((m) => ({ default: m.PdfStage })));
const OfficeStage = lazy(() =>
  import('./stages/OfficeStage').then((m) => ({ default: m.OfficeStage })),
);
import { MODE_COPY, stateLabel } from './state-copy';

export function Workspace({ id }: { id: string }) {
  const focus = useApp((s) => s.focus);
  const queueOpen = useApp((s) => s.queueOpen);
  const setQueueOpen = useApp((s) => s.setQueueOpen);
  const { record } = useSession(id);
  if (!record) return null;
  return (
    <m.div
      className={`work ${focus ? 'work--focus' : ''}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
    >
      <section className="work__main" aria-label="Preview and transfer">
        <SourceHeader id={id} />
        <div className="work__stage">
          <StageRouter id={id} />
        </div>
        <TransferPanel id={id} />
      </section>
      {queueOpen && (
        <button
          type="button"
          className="work__scrim"
          aria-label="Close file list"
          onClick={() => setQueueOpen(false)}
        />
      )}
      <aside
        className={`work__side ${queueOpen ? 'work__side--open' : ''}`}
        aria-label="Files on this device"
      >
        <Queue />
      </aside>
    </m.div>
  );
}

function SourceHeader({ id }: { id: string }) {
  const { record, snap } = useSession(id);
  if (!record) return null;
  const finalHost = snap?.finalHost && snap.finalHost !== record.host ? snap.finalHost : null;
  return (
    <header className="source">
      <KindIcon kind={record.kind} className="source__icon" />
      <div className="source__text">
        <h1 className="source__name" title={record.name}>
          {record.name}
        </h1>
        <p className="source__meta">
          <span className="mono">{record.host}</span>
          {finalHost && (
            <span>
              {' '}
              · served from <span className="mono">{finalHost}</span>
            </span>
          )}
          {record.contentType && (
            <span className="mono"> · {record.contentType.split(';')[0]}</span>
          )}
        </p>
      </div>
    </header>
  );
}

function StageRouter({ id }: { id: string }) {
  const { record, snap, saved, total, fraction } = useSession(id);
  if (!record) return null;
  const hasBytes = saved > 0;

  if (record.state === 'fatalError' || (record.state === 'recoverableError' && !hasBytes)) {
    return (
      <StatusStage
        kind={record.kind}
        title={record.lastError?.summary ?? 'Cannot continue'}
        body={record.lastError?.action}
      >
        {record.lastError && <ErrorActions id={id} error={record.lastError} />}
      </StatusStage>
    );
  }
  if (
    record.state === 'queued' ||
    record.state === 'probing' ||
    (!hasBytes && record.state === 'active')
  ) {
    return (
      <StatusStage
        kind={record.kind}
        title="Checking the link…"
        body="OneFlow is asking the host for the first bytes."
        busy
      />
    );
  }
  if (total === 0 || (snap?.identity?.totalSize === 0 && record.state === 'complete')) {
    return (
      <StatusStage kind="unknown" title="This file is empty" body="There is nothing to preview." />
    );
  }

  switch (record.kind) {
    case 'video':
      return <MediaStage key={id} id={id} kind="video" />;
    case 'audio':
      return <MediaStage key={id} id={id} kind="audio" />;
    case 'image':
      return <ImageStage key={id} id={id} />;
    case 'pdf':
      return (
        <Suspense fallback={<StatusStage kind="pdf" title="Opening the PDF…" busy />}>
          <PdfStage key={id} id={id} />
        </Suspense>
      );
    case 'text':
    case 'markdown':
    case 'json':
    case 'csv':
    case 'xml':
      return <TextStage key={id} id={id} kind={record.kind} />;
    case 'docx':
    case 'xlsx':
      return (
        <Suspense fallback={<StatusStage kind={record.kind} title="Reading the document…" busy />}>
          <OfficeStage key={id} id={id} kind={record.kind} />
        </Suspense>
      );
    default:
      return (
        <StatusStage
          kind={record.kind}
          title={
            record.container === null ? 'Reading the file header…' : 'No preview for this file type'
          }
          body={
            record.container === null
              ? undefined
              : record.state === 'complete'
                ? 'The file is saved on this device. Export the original to open it in another app.'
                : `The download continues${fraction !== null ? ` (${formatPercent(fraction)} saved)` : ''}. Export the original when it is complete.`
          }
          busy={record.container === null}
        />
      );
  }
}

function TransferPanel({ id }: { id: string }) {
  const { record, snap, total, saved, fraction, transferred, overhead, complete } = useSession(id);
  const feed = useApp((s) => s.feeds[id] ?? null);
  const setPanel = useApp((s) => s.setPanel);
  const pause = useApp((s) => s.pause);
  const resume = useApp((s) => s.resume);
  const setConfirmRemove = useApp((s) => s.setConfirmRemove);
  const setMode = useApp((s) => s.setMode);
  const toast = useApp((s) => s.toast);
  const [exporting, setExporting] = useState<number | null>(null);
  if (!record) return null;

  const live = record.state === 'active' || record.state === 'probing';
  const paused = record.state === 'paused';
  const rate = snap?.bytesPerSecond ?? null;
  const remaining = total !== null ? Math.max(0, total - saved) : null;
  const plan = decidePlan(record.container, complete, !engine.caps.mse);
  const sequential =
    snap && (snap.rangeProvenance === 'ignored' || snap.rangeProvenance === 'advertised-unproven');
  const swReady = 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
  const canExport = complete && total !== null && total > 0;

  const doExport = async () => {
    if (!canExport || total === null) return;
    setExporting(0);
    try {
      await exportOriginal(engine, id, record.name, total, setExporting);
      toast({
        kind: 'success',
        title:
          exportMode() === 'picker'
            ? 'Saved to your chosen location'
            : 'The browser is saving the file',
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'The file could not be saved.';
      toast({ kind: 'error', title: msg, body: 'Try again, or free some disk space.' });
    } finally {
      setExporting(null);
    }
  };

  return (
    <section className="transfer" aria-labelledby={`transfer-${id}`}>
      <div className="transfer__head">
        <h2 className="transfer__state" id={`transfer-${id}`}>
          <span className={`dot dot--${record.state}`} aria-hidden="true" />
          {stateLabel(record.state)}
        </h2>
        <div className="transfer__chips">
          {snap?.seekable && !complete && (
            <Chip title="The host answered a ranged request correctly, so jumping ahead fetches only what is needed.">
              Seekable
            </Chip>
          )}
          {sequential && !complete && (
            <Chip tone="warn" title="This host sends the file in order and cannot skip ahead.">
              Plays in order only
            </Chip>
          )}
          {(plan === 'wait-complete' || plan === 'unsupported-kind') && !complete && (
            <Chip tone="warn">Preview after download</Chip>
          )}
          {(record.kind === 'docx' || record.kind === 'xlsx') && (
            <Chip title="Text-first preview; formatting is simplified.">Limited preview</Chip>
          )}
          {complete && swReady && (
            <Chip tone="solid" title="The app and this file open without a network connection.">
              Offline ready
            </Chip>
          )}
          {complete && !swReady && <Chip tone="solid">Saved</Chip>}
        </div>
      </div>

      <dl className="figures">
        <Figure
          label="Saved"
          value={
            <>
              {formatBytes(saved)}
              {total !== null && <span className="figure__of"> of {formatBytes(total)}</span>}
              <span className="figure__pct"> {formatPercent(fraction)}</span>
            </>
          }
          hint="Unique bytes written to this device. Each byte is saved once."
        />
        <Figure
          label="Received"
          value={
            <>
              {formatBytes(transferred)}
              {overhead > 4096 && (
                <span className="figure__of"> · {formatBytes(overhead)} re-sent</span>
              )}
            </>
          }
          hint="Response bytes that crossed the network, including retries and aborted tails. Headers are not counted."
        />
        <Figure label="Speed" value={live ? formatRate(rate) : '—'} />
        <Figure
          label="Time left"
          value={
            live && remaining !== null
              ? formatEta(remaining, rate, snap?.counters.requests ? 6 : 0)
              : complete
                ? 'Done'
                : '—'
          }
        />
      </dl>

      {snap || complete ? (
        <div className="transfer__rail">
          <CoverageRail
            total={total}
            readable={snap?.readable ?? (total ? [{ start: 0, end: total }] : [])}
            inFlight={snap?.inFlight ?? []}
            feed={feed}
            label="Saved on this device"
          />
          {total !== null && total > 0 && (
            <div className="rail-scale mono" aria-hidden="true">
              <span>0</span>
              <span>{formatBytes(total)}</span>
            </div>
          )}
          <ul className="legend" aria-hidden="true">
            <li>
              <span className="legend__sw legend__sw--saved" />
              Saved
            </li>
            <li>
              <span className="legend__sw legend__sw--flight" />
              Writing now
            </li>
            <li>
              <span className="legend__sw legend__sw--feed" />
              Player is reading here
            </li>
          </ul>
        </div>
      ) : null}

      {record.lastError && record.state !== 'complete' && (
        <div className="notice notice--error" role="alert">
          <p className="notice__title">
            <Warning aria-hidden="true" />
            {record.lastError.summary}
          </p>
          <p className="notice__body">{record.lastError.action}</p>
          <ErrorActions id={id} error={record.lastError} />
        </div>
      )}

      <div className="transfer__controls">
        {!complete && (
          <fieldset className="modes">
            <legend className="modes__legend">Download mode</legend>
            <div
              className="seg seg--wide"
              role="radiogroup"
              aria-label="Download mode"
              aria-describedby={`mode-help-${id}`}
            >
              {(Object.keys(MODE_COPY) as DownloadMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={record.mode === m}
                  className="seg__btn"
                  onClick={() => setMode(id, m)}
                >
                  {MODE_COPY[m].name}
                </button>
              ))}
            </div>
            <p className="modes__help" id={`mode-help-${id}`}>
              {MODE_COPY[record.mode].help}
            </p>
          </fieldset>
        )}
        <div className="actions">
          {live && (
            <button type="button" className="btn btn--solid" onClick={() => pause(id)}>
              <Pause weight="fill" /> Pause
            </button>
          )}
          {(paused || (record.state === 'recoverableError' && saved > 0)) && (
            <button type="button" className="btn btn--solid" onClick={() => void resume(id)}>
              {paused ? <Play weight="fill" /> : <ArrowClockwise />}{' '}
              {paused ? 'Resume' : 'Try again'}
            </button>
          )}
          {canExport && (
            <button
              type="button"
              className="btn btn--solid"
              onClick={() => void doExport()}
              disabled={exporting !== null}
            >
              <DownloadSimple />
              {exporting !== null
                ? `Saving… ${total ? Math.floor((exporting / total) * 100) : 0}%`
                : 'Export original'}
            </button>
          )}
          <button type="button" className="btn" onClick={() => setPanel('details')}>
            <Info /> Details
          </button>
          <button type="button" className="btn btn--danger" onClick={() => setConfirmRemove(id)}>
            <Trash /> Delete
          </button>
        </div>
        {canExport && (
          <p className="transfer__note">
            {exportMode() === 'picker'
              ? 'Exports the original file, byte for byte, from this device. Nothing is downloaded again.'
              : 'This browser saves from device storage instead of a save dialog. Exporting can briefly need extra free disk space.'}
          </p>
        )}
      </div>
    </section>
  );
}

/** Recovery actions matched to the actual failure. UI-013 */
export function ErrorActions({ id, error }: { id: string; error: AppError }) {
  const resume = useApp((s) => s.resume);
  const setPanel = useApp((s) => s.setPanel);
  const setLinkNeeded = useApp((s) => s.setLinkNeeded);
  const setConfirmRestart = useApp((s) => s.setConfirmRestart);
  const setConfirmRemove = useApp((s) => s.setConfirmRemove);
  const code = error.code;

  return (
    <div className="actions actions--tight">
      {code === 'CORS_OR_NETWORK' && (
        <>
          <button type="button" className="btn btn--solid" onClick={() => void resume(id)}>
            <ArrowClockwise /> Try again
          </button>
          <button type="button" className="btn" onClick={() => setPanel('cors')}>
            Why this happens
          </button>
        </>
      )}
      {(code === 'HTTP_FORBIDDEN' || code === 'HTTP_EXPIRED' || code === 'HTTP_NOT_FOUND') && (
        <button type="button" className="btn btn--solid" onClick={() => setLinkNeeded(id)}>
          Paste a new link
        </button>
      )}
      {code === 'SOURCE_CHANGED' && (
        <>
          <button type="button" className="btn btn--solid" onClick={() => setConfirmRestart(id)}>
            Discard saved part and start over
          </button>
          <button type="button" className="btn" onClick={() => setConfirmRemove(id)}>
            Delete this file
          </button>
        </>
      )}
      {code === 'STORAGE_CORRUPT' && (
        <button type="button" className="btn btn--solid" onClick={() => setConfirmRestart(id)}>
          Start over
        </button>
      )}
      {(code === 'QUOTA_EXCEEDED' ||
        code === 'TRUNCATED_BODY' ||
        code === 'WORKER_CRASHED' ||
        code === 'RANGE_NOT_SATISFIABLE' ||
        code === 'BAD_CONTENT_RANGE' ||
        code === 'HTTP_STATUS' ||
        code === 'UNKNOWN') && (
        <button type="button" className="btn btn--solid" onClick={() => void resume(id)}>
          <ArrowClockwise /> Try again
        </button>
      )}
      {code === 'WEBPAGE_NOT_FILE' && (
        <button type="button" className="btn" onClick={() => setPanel('cors')}>
          What counts as a direct link?
        </button>
      )}
    </div>
  );
}
